/**
 * One definition of what a form control looks like.
 * ─────────────────────────────────────────────────────────────────────
 * modalTheme.js exists because an audit found eight header treatments
 * and three radius systems across fourteen modals. The same thing had
 * happened to the inputs, one file at a time:
 *
 *   TitleRequestModal         #cbd5e1  0.5rem 0.6rem    0.82rem
 *   DirectInterestModal       #cbd5e1  0.3rem 0.45rem   0.76rem
 *   PreliminaryTitleWorkflow  #cbd5e1  0.42rem 0.5rem   0.78rem
 *   AssetsUnderManagement     #94a3b8  7px 8px          12
 *   OwnerDashboard (offer)    #d1d5db  0.34rem 0.4rem   10pt
 *
 * Four border greys, five paddings, and four units for one measurement
 * — including a 10pt left over from a print stylesheet and a bare 12.
 * Nobody chose that; it is what happens when the next form is written
 * beside the last one instead of from a shared rule.
 *
 * The cost is not only that it looks untidy. A field that is 0.82rem in
 * one window and 0.76rem in the next is a field somebody re-reads, and
 * a border that changes grey between two forms on the same screen reads
 * as one of them being disabled.
 *
 * `radius: 0` is the one thing every one of them already agreed on, and
 * it stays — square is this app's vocabulary everywhere else too.
 */

/**
 * Two surfaces are deliberately NOT on this rule, and both have a
 * reason that survives reading:
 *
 *   OfferExhibitEditorPage  Georgia 10pt, because its fields sit inside
 *                           an on-screen copy of the printed Exhibit A.
 *                           Setting them in the app's sans would make
 *                           the editor stop looking like the document
 *                           it edits.
 *   the interest estimator  fields on navy, light text. A white field
 *                           in that table would be the brightest thing
 *                           on the page.
 *
 * They are listed here so the next audit does not "fix" them.
 */

/**
 * The focus ring, which an inline style cannot carry.
 *
 * `FORM_SCOPE_CLASS` goes on a container and covers every input, select
 * and textarea inside it; `FORM_CONTROL_CLASS` is the per-field escape
 * hatch. Prefer the scope: the forms it covers have fifty-odd fields
 * between them, and a per-field class is a class somebody forgets on
 * the fifty-first.
 *
 * The ring is navy, so it only reads against a light body. A dark
 * surface does not take the scope — see the two exceptions above.
 */
export const FORM_SCOPE_CLASS = 'app-form';
export const FORM_CONTROL_CLASS = 'app-field';

const BORDER = '#cbd5e1';
const BORDER_INVALID = '#b45309';
const TEXT = '#0f172a';
const TEXT_DISABLED = '#64748b';

/**
 * @param {object}  opts
 * @param {boolean} opts.compact   dense grids — the estimator's tables
 * @param {boolean} opts.disabled
 * @param {boolean} opts.invalid   the field is why the form will not submit
 * @param {object}  opts.extra     anything genuinely per-field: width, grid span
 */
export function formControl({ compact = false, disabled = false, invalid = false, extra = {} } = {}) {
  return {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    // Square, like every other surface in this app.
    borderRadius: 0,
    border: `1px solid ${invalid ? BORDER_INVALID : BORDER}`,
    padding: compact ? '0.28rem 0.4rem' : '0.45rem 0.55rem',
    fontSize: compact ? '0.74rem' : '0.82rem',
    // Inherit the family rather than letting the browser pick its own
    // for inputs — the reason a form can look like a different app from
    // the page it is on.
    fontFamily: 'inherit',
    background: disabled ? '#f8fafc' : '#ffffff',
    color: disabled ? TEXT_DISABLED : TEXT,
    cursor: disabled ? 'not-allowed' : undefined,
    ...extra,
  };
}

/**
 * The label above a control.
 *
 * Small, uppercase and quiet: on a form, the thing being read is what
 * has been typed, and a label drawn as loudly as its value makes a
 * filled form look like an empty one.
 */
export function formLabel(extra = {}) {
  return {
    display: 'block',
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: '#64748b',
    marginBottom: 4,
    ...extra,
  };
}

/** The sentence under a control that explains it. Never a warning. */
export function formHint(extra = {}) {
  return {
    marginTop: 4,
    fontSize: '0.7rem',
    lineHeight: 1.45,
    color: '#64748b',
    ...extra,
  };
}
